import {
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import PageTitle from "../../../components/pageTitle/PageTitle";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ListTodo, Power } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { useMediaQuery } from "@mui/material";
import { CommonConfirmationDialog } from "../../../components/DialogComponent/SupplyDataDialog";
import CommonContext from "../../../hooks/commonContext";
import AuthContext from "../../../hooks/authContext";
import { useNavigate } from "react-router";
import { useToast } from "../../../hooks/useToast";
import {
  getSettings,
  resetSettings,
  saveSettings,
  updateSettings,
} from "../../../services/apiservices/settings";

const ProofOfServiceSetting = () => {
  const [settings, setSettings] = useState({});
  const isMobile = useMediaQuery("(max-width:767px)");
  const [resetOpen, setResetOpen] = useState(false);
  const [disableDialog, setDisabledDialog] = useState(false);
  const [filterData, setFilterData] = useState();
  const commonContext = useContext(CommonContext);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { toast } = useToast();
  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;

  const taskBehaviorOptions = [
    {
      value: "low",
      label: "Low",
      description: "Employees will be randomly prompted for photos on ~1 in 20 visits (5% frequency).",
      frequency: 5,
    },
    {
      value: "medium",
      label: "Medium",
      description: "Employees will be randomly prompted for photos on ~1 in 10 visits (10% frequency).",
      frequency: 10,
    },
    {
      value: "high",
      label: "High",
      description: "Employees will be randomly prompted for photos on ~1 in 5 visits (20% frequency).",
      frequency: 20,
    },
  ];

  const fetchInitialData = async () => {
    try {
      commonContext?.setLoader(true);

      const [settingsRes] = await Promise.all([
        new Promise((resolve) =>
          getSettings("proofOfService_settings", companyId, (res) => resolve(res?.data))
        ),
      ]);

      const filteredData = settingsRes?.filter(
        (item) =>  !item.deleted
      );
      setFilterData(filteredData?.length ? filteredData : []);

      if (filteredData?.length > 0) {
        const taskOption = filteredData?.[0]?.taskOption || "low";
        const proofOfService = filteredData?.[0]?.proofOfService || false;
        setSettings({ taskOption: taskOption, proofOfService: proofOfService });
      } else {
        setSettings({});
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setSettings({});
    } finally {
      commonContext?.setLoader(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchInitialData is stable, no need to include in deps

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveData = async () => {
    commonContext?.setLoader(true);

    const dataToSave = {
      taskOption: settings?.taskOption,
      proofOfService: settings?.proofOfService,
      companyId: companyId,
    };

    const data = await saveSettings(dataToSave, "proofOfService_settings");

    if (data.status === true) {
      await fetchInitialData();

      toast?.({ title: "Success", description: "Setting Saved Successfully" });
      commonContext?.setLoader(false);
    }
    commonContext?.setLoader(false);
  };

  const updateData = async () => {
    commonContext?.setLoader(true);
    const dataToSave = {
      taskOption: settings?.taskOption,
      proofOfService: settings?.proofOfService,
    };

    const id = filterData[0]?.id;

    const details = await updateSettings(
      dataToSave,
      "proofOfService_settings",
      id
    );

    if (details.status === true) {
      await fetchInitialData();

      toast?.({ title: "Success", description: "Setting Update Successfully" });
      commonContext?.setLoader(false);
    }
    commonContext?.setLoader(false);
  }

  const handleUpdateData = async () => {
    if (settings?.proofOfService === false) {
      setDisabledDialog(true);
      return;
    } else {
      await updateData();
    }
  };

  const handleUpdateConfirm = async () => {
    setDisabledDialog(false);
    await updateData();
  }

  const handleUpdateClose = () => {
    setDisabledDialog(false);
  };

  const handleResetClose = () => {
    setResetOpen(false);
  };

  const handleResetConfirm = async () => {
    setResetOpen(false);
    commonContext?.setLoader(true);

    const id = filterData[0]?.id;

    try {
      const [details] = await Promise.all([
        resetSettings("proofOfService_settings", id),
      ]);

      if (details.status === true) {
        await fetchInitialData();
        toast?.({
          title: "Success",
          description: "Setting Reset Successfully",
        });
        commonContext?.setLoader(false);
      }

      commonContext?.setLoader(false);
    } catch (error) {
      console.error("Error resetting settings:", error);
      commonContext?.setLoader(false);
    }
  };

  const handleReset = () => {
    if (filterData?.length > 0) {
      setResetOpen(true);
    } else {
      setSettings({});
    }
  };

  return (
    <>
      <Container>
        <PageTitle
          pageTitle={`Proof Of Service Settings`}
          showBreadcrumb={[
            { label: "Home", to: "/" },
            { label: "Settings", to: "/account/settings" },
          ]}
          BreadCrumbCurrentPageTitle={"Proof Of Service"}
          showLastSeen={true}
        />

        <div
          className={
            isMobile
              ? "sectionBox p-8 dark:bg-[#1A1F2C]"
              : "sectionBox dark:bg-[#1A1F2C] px-24 py-16"
          }
        >
          <Card className="w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
            <CardHeader className={isMobile ?  "pb-10 pt-16 ph-20" : "pb-10 pt-16 px-10"}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className={`p-16 rounded-md ${
                      settings.proofOfService
                        ? "bg-green-50 dark:bg-green-900/20"
                        : "bg-gray-50 dark:bg-gray-800"
                    }`}
                  >
                    <Power
                      className={`h-6 w-6 ${
                        settings.proofOfService
                          ? "text-green-500 dark:text-green-400"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                    />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-semibold">
                      Global Proof of Service
                    </CardTitle>
                    <CardDescription className="mt-5">
                      Enable or disable Proof of Service across the entire
                      application
                    </CardDescription>
                  </div>
                </div>

                <Badge
                  variant={settings.proofOfService ? "default" : "secondary"}
                  className={`${
                    settings.proofOfService ? "bg-green-500" : ""
                  } px-4 py-2`}
                >
                  {settings.proofOfService ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className={isMobile ? "ph-20 pb-12" :"px-10 pb-20"}>
              <div className="mt-16 w-full border border-gray-200 dark:border-gray-700 rounded-lg p-20">
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1">
                    <div className="font-medium text-center text-lg">
                      Enable Proof of Service
                    </div>

                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-10 w-full text-center">
                      When enabled, Proof of Service features will be available
                      across the application. When disabled, all PoS-related UI
                      will be hidden.
                    </div>
                  </div>

                  <div className="pl-40">
                    <Switch
                      checked={settings.proofOfService}
                      onCheckedChange={(checked) =>
                        handleSettingChange("proofOfService", checked)
                      }
                    />
                  </div>
                </div>
              </div>

              {!settings.proofOfService && (
                <div className="mt-10 p-20 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                    <strong>Note:</strong> When Proof of Service is disabled
                    globally, all PoS-related features including client
                    settings, lists, reports, and alerts will be hidden or
                    disabled throughout the application.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-40">
            <Card
              className={
                !settings.proofOfService
                  ? "opacity-50 pointer-events-none w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"
                  : "w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"
              }
            >
              <CardHeader className="pb-10 pt-16 px-10">
                <div className="flex items-start space-x-4">
                  <div className="p-16 bg-purple-50 dark:bg-purple-900/20 rounded-md">
                    <ListTodo className="h-6 w-6 text-purple-500 dark:text-purple-400" />
                  </div>

                  <div>
                    <CardTitle className="text-xl font-semibold">
                      Proof of Service Frequency
                    </CardTitle>

                    <CardDescription className="mt-5">
                      Choose how often employees are asked to submit photos as proof of service during visits.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-10 pb-12">
                <div className="font-medium mb-8 mt-12">
                  Default Photo Prompt Frequency
                </div>
                <FormControl
                  component="fieldset"
                  className="w-full dark:bg-[#1A1F2C]"
                >
                  <RadioGroup
                    disabled={!settings.proofOfService}
                    value={settings?.taskOption}
                    className="space-y-3 mt-8"
                    onValueChange={(value) =>
                      handleSettingChange("taskOption", value)
                    }
                  >
                    {taskBehaviorOptions?.map((option) => {
                      const isSelected = settings?.taskOption === option?.value;
                      return (
                        <div
                          key={option?.value}
                          className={`
                                flex items-start space-x-3 p-24 rounded-xl border transition-all cursor-pointer dark:bg-[#1A1F2C]
                                ${
                                  isSelected
                                    ? "border-green-600 bg-green-50"
                                    : "border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 dark:bg-[#1A1F2C]"
                                }
                            `}
                          onClick={() =>
                            handleSettingChange("taskOption", option?.value)
                          }
                        >
                          <FormControlLabel
                            value={option?.value}
                            id={option?.value}
                            control={<Radio color="primary" checked={isSelected} />}
                            label={
                              <div className="flex flex-col">
                                <span className="font-semibold text-[17px] text-gray-900 dark:text-gray-100 leading-tight">
                                  {option?.label}
                                </span>

                                <p className="text-[14px] text-gray-600 dark:text-gray-400 mt-4 leading-relaxed">
                                  {option?.description}
                                </p>
                              </div>
                            }
                          />
                        </div>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
                <div className="mv-20">
                  This setting controls how often employees are prompted for proof of service photos. You can override this per client if needed.
                </div>
              </CardContent>
            </Card>
          </div>
        <div className="mv-15 d-flex flex-center flex-justify-end">
          <Button
            variant="contained"
            style={{
              boxShadow: "none",
              backgroundColor: "#078037",
              color: "white",
            }}
            onClick={filterData?.length > 0 ? handleUpdateData : handleSaveData}
          >
            {filterData?.length > 0 ? "Update Changes" : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            style={{ marginLeft: 10, boxShadow: "none" }}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            className="dark:text-white"
            style={{ marginLeft: 10, boxShadow: "none" }}
            onClick={() => navigate("/account/settings")}
          >
            Cancel
          </Button>
        </div>
        </div>

        {resetOpen && (
          <CommonConfirmationDialog
            open={resetOpen}
            onConfirm={handleResetConfirm}
            onClose={handleResetClose}
            title={"Setting Reset"}
            message={
              "Resetting the industry will impact future appointments linked to its tasks. Existing appointments won't be affected, but future ones may show missing or incorrect tasks. Are you sure you want to proceed?"
            }
            buttonTitle={"Yes"}
          />
        )}

        {disableDialog && (
          <CommonConfirmationDialog
            open={disableDialog}
            onConfirm={handleUpdateConfirm}
            onClose={handleUpdateClose}
            title={"Disable Proof of Service?"}
            message={ "This will immediately disable photo documentation for all clients."}
            meessge1={"All Proof of Service settings — including default frequency and any client-specific overrides — will be permanently removed. Employees will no longer be prompted to take photos during visits."}
            buttonTitle={"Disable Feature"}
          />
        )}
      </Container>
    </>
  );
};

export default ProofOfServiceSetting;
